import * as classNames from "classnames";
import * as React from "react";
import * as DTOS from "../../../FeralScan.dtos";
import * as DashboardActionCreators from "../../../actions/dashboard.actions";
import * as SurveyActionCreators from "../../../actions/survey.actions";
import { controlColumnClass, labelColumnClass } from "./consts";
import Attachment from "./controls/Attachment";
import CheckboxControl from "./controls/CheckboxControl";
import DateTimeControl from "./controls/DateTimeControl";
import FileUploadControl from "./controls/FileUploadControl";
import MapLocationControl from "./controls/MapLocationControl";
import MonthYearControl from "./controls/MonthYearControl";
import PlotSelectControl from "./controls/PlotSelectControl";
import RadioButtonControl from "./controls/RadioButtonControl";
import SelectControl from "./controls/SelectControl";
import TextAreaControl from "./controls/TextAreaControl";
import TextBoxControl from "./controls/TextBoxControl";
import OldDateTimeControl from "./controls/OldDateTimeControl";

export type FormItemProps = {
    description?: string;
    formItemType: string;
    mandatory: boolean;
    name: string;
    model: string;
    attachment?: any;
    enableAttachments?: boolean;
    questionIndex?: number;
    readOnly?: boolean;
    formItemId?: number;
    formTemplateItemId: number;
    options?: DTOS.QuestionOption[];
    attachments?: any[];
    attachmentsToSave?: any[];
    survey?: DTOS.Survey;
    value?: string;
    hideRow?: boolean;
    uploadingFile?: boolean;
    userPlots?: DTOS.Plot[];
    selectedPlotId?: number;
    mapMarkerComponent: any;
};

export type FormItemActions = {
    error: typeof DashboardActionCreators.errorRequest;
    onChange?(value: string): void;
    onDrop?: Function;
    onDropAttachment?: Function;
    onDeleteAttachment?: Function;
    setQuestionValue?: typeof SurveyActionCreators.setQuestionValueRequest;
    uploadFiles?: typeof SurveyActionCreators.uploadFilesRequest;
};

export type FormItemPropsAndActions = FormItemProps & FormItemActions;

class FormItem extends React.PureComponent<FormItemPropsAndActions, any> {
    render() {
        const dateTimeDefaultDay = "01";

        let editor = undefined;

        let name = `${this.props.name}`;

        let attachments = undefined;
        let attachmentsToSave = undefined;

        if (
            this.props.attachmentsToSave &&
            this.props.attachmentsToSave.length > 0
        ) {
            attachmentsToSave = (
                <div className="d-inline-block">
                    {this.props.attachmentsToSave.map(attachment => (
                        <Attachment
                            key={attachment.preview}
                            fileName={attachment.name}
                            fileType={attachment.type}
                            src={attachment.preview}
                            loading
                        />
                    ))}
                </div>
            );
        }
        if (this.props.attachments && this.props.attachments.length > 0) {
            attachments = (
                <div className="d-inline-block">
                    {this.props.attachments.map(attachment => (
                        <Attachment
                            key={attachment.attachmentId}
                            fileName={attachment.fileName}
                            fileType={attachment.fileType}
                            attachmentId={attachment.attachmentId}
                            onDelete={this.props.onDeleteAttachment.bind(
                                null,
                                this.props.formTemplateItemId
                            )}
                        />
                    ))}
                </div>
            );
        }

        const controlClass = this.props.hideRow ?
            "" :
            controlColumnClass;

        switch (this.props.formItemType) {
            case "Combo":
                editor = (
                    <SelectControl
                        className={classNames(controlClass)}
                        model={`${this.props.model}.value`}
                        mandatory={this.props.mandatory}
                        onChange={this.props.onChange}
                        options={this.props.options}
                        readOnly={this.props.readOnly}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </SelectControl>
                );
                break;
            case "MultipleChoice":
                editor = (
                    <RadioButtonControl
                        attachment={this.props.attachment}
                        name={name}
                        description={this.props.description}
                        model={this.props.model}
                        mandatory={this.props.mandatory}
                        mapMarkerComponent={this.props.mapMarkerComponent}
                        options={this.props.options}
                        radioButtonClassName="pl-md-1"
                        readOnly={this.props.readOnly}
                        value={this.props.value}
                        error={this.props.error}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </RadioButtonControl>
                );
                break;
            case "EmailTextBox":
                editor = (
                    <TextBoxControl
                        attachment={this.props.attachment}
                        name={name}
                        description={this.props.description}
                        model={`${this.props.model}.value`}
                        mandatory={this.props.mandatory}
                        readOnly={this.props.readOnly}
                        type="email"
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </TextBoxControl>
                );
                break;
            case "NumericTextBox":
                editor = (
                    <TextBoxControl
                        attachment={this.props.attachment}
                        name={name}
                        description={this.props.description}
                        model={`${this.props.model}.value`}
                        mandatory={this.props.mandatory}
                        readOnly={this.props.readOnly}
                        type="number"
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </TextBoxControl>
                );
                break;
            case "TextBox":
                editor = (
                    <TextBoxControl
                        className={classNames({ "pl-md-3": !this.props.hideRow }, controlClass)}
                        attachment={this.props.attachment}
                        name={name}
                        description={this.props.description}
                        model={`${this.props.model}.value`}
                        mandatory={this.props.mandatory}
                        readOnly={this.props.readOnly}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </TextBoxControl>
                );
                break;
            case "TextArea":
                editor = (
                    <TextAreaControl
                        className={classNames("textarea-container")}
                        attachment={this.props.attachment}
                        name={name}
                        description={this.props.description}
                        model={`${this.props.model}.value`}
                        mandatory={this.props.mandatory}
                        readOnly={this.props.readOnly}
                        rows={5}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </TextAreaControl>
                );
                break;
            case "DateTime":
                editor = (
                    <DateTimeControl
                        //attachment={this.props.attachment}
                        //name={name}
                        //description={this.props.description}
                        model={`${this.props.model}.value`}
                        dateTitle={name.split("|")[0]}
                        timeTitle={name.split("|")[1]}
                        mandatory={this.props.mandatory}
                        readOnly={this.props.readOnly}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </DateTimeControl>
                );
                break;
            case "Date":
                editor = (
                    <OldDateTimeControl
                        //attachment={this.props.attachment}
                        //name={name}
                        //description={this.props.description}
                        model={`${this.props.model}.value`}
                        mandatory={this.props.mandatory}
                        readOnly={this.props.readOnly}
                        input={true}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </OldDateTimeControl>
                );
                break;
            case "Checkbox":
                editor = (
                    <CheckboxControl
                        attachment={this.props.attachment}
                        name={name}
                        description={this.props.description}
                        model={`${this.props.model}.value`}
                        mandatory={this.props.mandatory}
                        readOnly={this.props.readOnly}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </CheckboxControl>
                );
                break;
            case "MonthYearSelect":
                editor = (
                    <MonthYearControl
                        attachment={this.props.attachment}
                        selectClassName="pl-md-3"
                        controlOrder={'monthyear'}
                        defaultDay={dateTimeDefaultDay}
                        description={this.props.description}
                        mandatory={this.props.mandatory}
                        model={`${this.props.model}.value`}
                        name={name}
                        questionIndex={this.props.questionIndex}
                        readOnly={this.props.readOnly}
                        survey={this.props.survey}
                        setQuestionValue={this.props.setQuestionValue}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </MonthYearControl>
                );
                break;
            case "YearMonthSelect":
                editor = (
                    <MonthYearControl
                        attachment={this.props.attachment}
                        selectClassName="pl-md-3"
                        controlOrder={'yearmonth'}
                        defaultDay={dateTimeDefaultDay}
                        description={this.props.description}
                        mandatory={this.props.mandatory}
                        model={`${this.props.model}.value`}
                        name={name}
                        questionIndex={this.props.questionIndex}
                        readOnly={this.props.readOnly}
                        survey={this.props.survey}
                        setQuestionValue={this.props.setQuestionValue}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </MonthYearControl>
                );
                break;
            case "PlotSelect":
                editor = (
                    <PlotSelectControl
                        attachment={this.props.attachment}
                        description={this.props.description}
                        mandatory={this.props.mandatory}
                        model={`${this.props.model}.value`}
                        plots={this.props.userPlots}
                        readOnly={this.props.readOnly}
                        selectClassName="pl-md-3"
                        onChange={this.props.onChange}
                        setQuestionValue={this.props.setQuestionValue}
                        questionIndex={this.props.questionIndex}
                        overrideValue={this.props.selectedPlotId}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </PlotSelectControl>
                );
                break;
            case "MapLocation":
                editor = (
                    <MapLocationControl
                        attachment={this.props.attachment}
                        className={classNames("map-container")}
                        description={this.props.description}
                        mandatory={this.props.mandatory}
                        mapMarkerComponent={this.props.mapMarkerComponent}
                        model={`${this.props.model}.value`}
                        name={name}
                        questionIndex={this.props.questionIndex}
                        survey={this.props.survey}
                        setQuestionValue={this.props.setQuestionValue}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </MapLocationControl>
                );
                break;
            case "ImageUpload":
                editor = (
                    <FileUploadControl
                        model={`${this.props.model}.value`}
                        questionIndex={this.props.questionIndex}
                        survey={this.props.survey}
                        type="image"
                        error={this.props.error}
                        uploadFiles={this.props.uploadFiles}
                        className={classNames("fileupload-container")}
                        uploadingFile={this.props.uploadingFile}
                    >
                        <div>
                            {attachments}
                            {attachmentsToSave}
                        </div>
                    </FileUploadControl>
                );
                break;
            default:
                editor = (
                    <div>
                        <p>
                            Could not resolve question type
                        </p>
                        <p>
                            This was a {this.props.formItemType}
                        </p>
                    </div>
                );
                break;
        }

        const labelClass = this.props.formItemType == "MapLocation" || this.props.formItemType == "TextArea" || this.props.formItemType == "ImageUpload" ?
            "" :
            labelColumnClass;

        const isRow = this.props.formItemType != "YearMonthSelect" &&
            this.props.formItemType != "MapLocation" &&
            this.props.formItemType != "TextArea" &&
            this.props.formItemType != "ImageUpload";

        if (this.props.hideRow) {
            return <div className="form-control-container mt-3">
                {editor}
            </div>
        }

        return <div className={classNames("form-control-container mt-3", { "d-none": this.props.formItemType == "PlotSelect" })}>
            <div className={classNames("mb-3", { "row": isRow })}>
                {
                    name && name.length > 0 && name.indexOf("|") == -1 ? <label className={classNames("lead my-0", labelClass)}>{name}</label> : undefined
                }
                {editor}
            </div>
        </div>;
    }
}

export default FormItem;
